
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, Users, Activity, CheckCircle2, Clock, Search, Plus, Zap, Euro } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { UserHeader } from "@/components/UserHeader";
import { AttendanceQuickRegister } from "@/components/AttendanceQuickRegister";
import { AttendanceHistory } from "@/components/AttendanceHistory";
import { AttendanceStats } from "@/components/AttendanceStats";
import { AttendanceCalendar } from "@/components/AttendanceCalendar";
import { ActivityCard } from "@/components/ActivityCard";
import { ActivityForm } from "@/components/ActivityForm";
import { StudentList } from "@/components/StudentList";
import { useActivities, useAttendanceStats } from "@/hooks/useSupabaseData";
import { BulkStudentImporter } from '@/components/BulkStudentImporter';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { StudentForm } from "@/components/StudentForm";
import { MobileQuickAttendance } from "@/components/MobileQuickAttendance";
import { FinancesDashboard } from "@/components/FinancesDashboard";
import { BillingReports } from "@/components/BillingReports";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AppSettingsManager } from "@/components/settings/AppSettingsManager";
import { Settings } from "lucide-react";

export default function Dashboard() {
  const { isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState(isAdmin() ? "dashboard" : "attendance");
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [showStudentForm, setShowStudentForm] = useState(false);
  const { activities: todayActivities, loading: activitiesLoading, refetch: refetchActivities } = useActivities();
  const { stats: todayStats, loading: statsLoading } = useAttendanceStats();

  const recentAttendance = [
    { student: "María González", activity: "Fútbol", time: "16:05", status: "present" },
    { student: "Luis Rodríguez", activity: "Teatro", time: "17:02", status: "present" },
    { student: "Sofia Martín", activity: "Fútbol", time: "16:08", status: "present" },
    { student: "Diego Morales", activity: "Ajedrez", time: "16:32", status: "present" }
  ];

  // Navigation items based on user role
  const getNavigationItems = () => {
    if (isAdmin()) {
      return [
        { id: "dashboard", label: "Dashboard", icon: Activity },
        { id: "attendance", label: "Asistencia", icon: CheckCircle2 },
        { id: "quick-register", label: "Registro Rápido", icon: Zap },
        { id: "students", label: "Alumnos", icon: Users },
        { id: "activities", label: "Actividades", icon: CalendarDays },
        { id: "finances", label: "Finanzas", icon: Euro },
        { id: "stats", label: "Estadísticas", icon: Activity },
        { id: "settings", label: "Configuración", icon: Settings }
      ];
    } else {
      return [
        { id: "attendance", label: "Asistencia", icon: CheckCircle2 },
        { id: "quick-register", label: "Registro Rápido", icon: Zap }
      ];
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <UserHeader />
      
      {/* Navigation */}
      <nav className="border-b bg-card">
        <div className="container mx-auto px-6">
          <div className="flex space-x-8">
            {getNavigationItems().map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-2 border-b-2 transition-fast ${
                  activeTab === tab.id
                    ? "border-primary text-primary font-medium"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8">
        {activeTab === "dashboard" && isAdmin() && (
          <div className="space-y-8">
            {/* Resumen del día */}
            <Card className="shadow-card">
              <CardHeader>
                <CardTitle className="text-2xl text-center">Dashboard Administrativo</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-center text-muted-foreground">
                  Resumen completo del sistema de gestión de extraescolares.
                </p>
              </CardContent>
            </Card>

            {/* Actividades de Hoy */}
            <Card className="shadow-card">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <CalendarDays className="w-5 h-5 mr-2 text-primary" />
                  Actividades de Hoy
                </CardTitle>
              </CardHeader>
              <CardContent>
                {activitiesLoading ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Cargando actividades...
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {todayActivities.map((activity: any) => (
                      <ActivityCard key={activity.id} activity={activity} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === "stats" && isAdmin() && (
          <div className="space-y-6">
            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="bg-gradient-card shadow-card hover:shadow-lg transition-smooth">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Alumnos Hoy</CardTitle>
                  <Users className="h-4 w-4 text-primary" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {statsLoading ? "..." : todayStats.totalStudents}
                  </div>
                  <p className="text-xs text-success">
                    +12% desde ayer
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-card shadow-card hover:shadow-lg transition-smooth">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Presentes</CardTitle>
                  <CheckCircle2 className="h-4 w-4 text-success" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-success">
                    {statsLoading ? "..." : todayStats.presentStudents}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    de {statsLoading ? "..." : todayStats.totalStudents} alumnos
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-card shadow-card hover:shadow-lg transition-smooth">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Actividades Hoy</CardTitle>
                  <Activity className="h-4 w-4 text-primary" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {statsLoading ? "..." : todayStats.totalActivities}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {statsLoading ? "..." : todayStats.activeActivities} activas
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-card shadow-card hover:shadow-lg transition-smooth">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Tiempo Promedio</CardTitle>
                  <Clock className="h-4 w-4 text-primary" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">1.5h</div>
                  <p className="text-xs text-muted-foreground">
                    por actividad
                  </p>
                </CardContent>
              </Card>
            </div>

            <AttendanceStats />
          </div>
        )}

        {activeTab === "attendance" && (
          <div className="space-y-6">
            <Card className="shadow-card">
              <CardHeader>
                <CardTitle className="text-2xl text-center">
                  {isAdmin() ? "Gestión de Asistencia" : "Registro de Asistencia"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-center text-muted-foreground mb-6">
                  {isAdmin() 
                    ? "Gestiona la asistencia de los estudiantes a las actividades."
                    : "Registra la asistencia de los estudiantes a las actividades."
                  }
                </p>
              </CardContent>
            </Card>
            <AttendanceHistory />
          </div>
        )}

        {activeTab === "students" && isAdmin() && (
          <div className="space-y-6">
            <Card className="shadow-card">
              <CardHeader>
                <CardTitle>Gestión de Alumnos</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-6">
                  Administra la información de los estudiantes del sistema.
                </p>
                <div className="flex gap-2 mb-4">
                  <Button onClick={() => setShowBulkImport(true)}>
                    Importar alumnos
                  </Button>
                  <Button onClick={() => setShowStudentForm(true)}>
                    Nuevo alumno
                  </Button>
                </div>
              </CardContent>
            </Card>
            <StudentList />
            <StudentForm
              open={showStudentForm}
              onOpenChange={setShowStudentForm}
              onSuccess={() => {
                setShowStudentForm(false);
              }}
            />
            <Dialog open={showBulkImport} onOpenChange={setShowBulkImport}>
              <DialogContent className="w-full max-w-4xl">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Importar alumnos
                  </DialogTitle>
                </DialogHeader>
                <BulkStudentImporter simple />
              </DialogContent>
            </Dialog>
          </div>
        )}

        {activeTab === "activities" && isAdmin() && (
          <div className="space-y-6">
            <Card className="shadow-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Gestión de Actividades</CardTitle>
                  <div className="flex gap-2">
                  <Button onClick={() => setShowActivityForm(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Nueva Actividad
                  </Button>
                    <Button onClick={() => setShowBulkImport(true)}>
                      <Users className="w-4 h-4 mr-2" />
                      Importar alumnos
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {activitiesLoading ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Cargando actividades...
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {todayActivities.map((activity: any) => (
                      <ActivityCard 
                        key={activity.id} 
                        activity={activity} 
                        showActions 
                        onUpdate={() => {
                          refetchActivities();
                        }}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Formulario de nueva actividad */}
            <ActivityForm
              open={showActivityForm}
              onOpenChange={setShowActivityForm}
              onSuccess={() => {
                refetchActivities();
                setShowActivityForm(false);
              }}
            />

            {/* Modal de importación masiva de alumnos */}
            <Dialog open={showBulkImport} onOpenChange={setShowBulkImport}>
              <DialogContent className="w-full max-w-6xl">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Importar alumnos por Excel
                  </DialogTitle>
                </DialogHeader>
                <BulkStudentImporter />
              </DialogContent>
            </Dialog>
          </div>
        )}

        {activeTab === "quick-register" && (
          <div className="space-y-6">
            <Card className="shadow-card">
              <CardHeader>
                <CardTitle className="text-2xl text-center flex items-center justify-center gap-2">
                  <Zap className="w-6 h-6 text-primary" />
                  Registro Rápido de Asistencia
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-center text-muted-foreground mb-6">
                  Registra la asistencia de los estudiantes de forma rápida y sencilla.
                </p>
              </CardContent>
            </Card>
            
            <Tabs defaultValue="mobile" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="mobile">Registro Móvil</TabsTrigger>
                <TabsTrigger value="calendar">Registro con Calendario</TabsTrigger>
              </TabsList>
              
              <TabsContent value="mobile" className="mt-6">
                <MobileQuickAttendance />
              </TabsContent>
              
              <TabsContent value="calendar" className="mt-6">
                <AttendanceCalendar />
              </TabsContent>
            </Tabs>
          </div>
        )}

        {activeTab === "finances" && isAdmin() && (
          <FinancesDashboard />
        )}

        {activeTab === "settings" && isAdmin() && (
          <div className="space-y-6">
            <AppSettingsManager />
          </div>
        )}
      </main>
    </div>
  );
}
